import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button, Field, FocusedForm, Loading, Page, PageHeader, Section, TechnicalDetails, Unavailable, useToaster, Waiting } from '@beyond-js/ui/react';
import { config } from '@snapshots/config';
import { Condition, Describe, Links, Policy, type Capture } from '@snapshots/domain';
import { Address, Copies, Origin, useFrame, usePlace } from '@snapshots/navigation';
import { usePresence } from '@snapshots/session';
import type { Refusal } from '@snapshots/transport';
import { Failure, Missing, OutageScope, Outages, Problems, Updated, useMissing, type Missed } from '@snapshots/ui';
import { SnapshotStatus } from './columns';
import { CaptureFacts } from './facts';
import { Lifecycle } from './lifecycle';
import { Packages } from './packages';
import { Ended, Pending } from './pending';
import { Snapshot } from './snapshot';

/**
 * One snapshot: what it holds and what to do with it next (SNP-02, SNP-18). Its header names it with one
 * status (D42), the project it belongs to as a link, and its next steps — Publish on CDN, Open in Packages,
 * Compare with the previous one, and the More menu (`Lifecycle`). The main column holds its packages
 * (`Packages`: files, a text viewer, Download), its publication settings when it has any, and its title; the
 * side panel what is known about it (`CaptureFacts`). The tab's title names it and its state (D50), so the end
 * of a snapshot made while the person was elsewhere reaches the tab. The way back returns to the list the
 * person came from, with its search and page, or else to the snapshot's project.
 *
 * Another snapshot is another page: what was read for one is never shown for the next.
 */
export function CaptureView() {
	const { id = '' } = useParams();
	return <SnapshotPage key={id} id={id} />;
}

/**
 * The reads of a snapshot nobody answered are said once (D163): one line on the H1's edge with who did not
 * answer, when it last tried and one Try again. With nothing read yet, the line is the page's body; over a
 * snapshot read earlier, "Last updated" says when, and a package's files or a file nobody answered say "Not
 * available right now" in their own place. A refusal, a session and a change keep their own answers.
 */
function SnapshotPage({ id }: { id: string }) {
	const { text, captures, identity, date, short, language, unsaved, size } = usePresence();
	const { arrival, names, descriptor } = useFrame();
	const snapshot = Snapshot.use(id);
	const { capture, refused, missed } = snapshot;
	// What the page's regions found unanswered: a package's files and a file
	const missing = useMemo(() => new Missing(), []);
	const regional = useMissing(missing);
	const summary = text('capture.configuration');
	const configuration = useMemo(() => ({ ...TechnicalDetails.labels[language], summary }), [language, summary]);
	const { state: came } = useLocation();
	// Only a path of this application, as the list set it
	const from = typeof came?.from === 'string' && came.from.startsWith('/') && !came.from.startsWith('//') ? (came.from as string) : undefined;
	const toasts = useToaster(Copies.toasts(language));
	// A change's own answer: refused, answered as unavailable, or unknown when it got none
	const [problem, setProblem] = useState<Refusal>();
	// The last change, for the person's own Try again after the service answered that nothing was changed (D186):
	// only a change with no action of its own in view (restoring), since a failure never restates its button
	const last = useRef<() => Promise<void>>();
	const [busy, setBusy] = useState(false);
	const [previous, setPrevious] = useState<string>();
	// A snapshot seen pending keeps its awaited card once it completes, so the card can say it ended (D50)
	const awaited = useRef(false);
	if (capture?.state === 'pending') awaited.current = true;
	// Known once the snapshot is read, or once reading it failed and it names no place
	usePlace(capture ? { project: capture.project, organization: capture.organization } : refused || missed ? {} : undefined);

	// The snapshot of the same project made just before, for "Compare with the previous one"
	useEffect(() => {
		if (!capture) return;
		let live = true;
		captures.find({ project: capture.project, limit: 50 }).then(
			found => {
				const older = found.captures.filter(entry => entry.state === 'active' && entry.id !== capture.id && entry.created < capture.created);
				if (live) setPrevious(older[0]?.id);
			},
			() => undefined
		);
		return () => {
			live = false;
		};
	}, [captures, capture?.id, capture?.project, capture?.created]);

	// The tab names the snapshot and its state, so its end reaches a person in another tab (D50)
	const standing = capture ? Condition.of(capture) : undefined;
	const named = capture ? Describe.title(capture, text, short) : undefined;
	useEffect(() => {
		const state = standing === 'deleted' ? text('captures.state.deleted') : standing && text(`capture.standing.${standing}`);
		document.title = named && state ? text('page.capture', { title: named, state }) : text('page.list');
	}, [named, standing, text]);

	// A title typed and not saved is held, so signing out asks before losing it (Q09)
	const typed = Boolean(capture && capture.state !== 'deleted' && Policy.edits(identity, capture.organization) && snapshot.title.trim() !== (capture.title ?? ''));
	useEffect(() => {
		const key = `title:${id}`;
		if (typed) unsaved.hold(key, named ?? '');
		else unsaved.release(key);
		return () => unsaved.release(key);
	}, [unsaved, id, typed, named]);

	const act = async (work: () => Promise<Capture>, outcome: (capture: Capture) => string, again = false) => {
		last.current = again ? () => act(work, outcome, again) : undefined;
		setBusy(true);
		setProblem(undefined);
		try {
			const changed = await work();
			snapshot.keep(changed);
			// One outcome at a time: a new one replaces the last
			toasts.clear();
			toasts.show(outcome(changed));
		} catch (cause) {
			const refusal = Problems.refusal(cause);
			// Not sent while the session was ended: the form stays as it was, and `Session` speaks for the session
			if (refusal.quiet) return;
			setProblem(refusal);
			// The change may have been made: what is shown is read again rather than assumed
			if (refusal.uncertain) snapshot.again();
		} finally {
			setBusy(false);
		}
	};
	const settle = (changed: Capture | undefined, outcome?: string) => {
		if (changed) snapshot.keep(changed);
		else snapshot.again();
		toasts.clear();
		if (outcome) toasts.show(outcome);
	};

	// The snapshot nobody answered, and what its regions found unanswered, are one line (D163)
	const reads: Missed[] = [...(missed ? [{ key: 'snapshot', title: text('outage.read.capture'), refusal: missed, run: snapshot.load }] : []), ...regional];
	// The page every state is drawn in: the arrival line, the header, the panel beside the main column, and the
	// one line at the column's start, in one place for every state, so a line that ends takes focus to the heading
	const crumbs = [{ label: text('nav.captures'), href: from ?? (capture ? Address.of({ project: capture.project }) : '/') }];
	const page = (children: ReactNode, header: { title: string; status?: ReactNode; facts?: ReactNode; actions?: ReactNode }, aside?: ReactNode) => (
		<Page template="detail" width="standard" arrival={<Origin project={capture?.project} organization={capture?.organization} waiting={!capture && !refused && !missed} />} header={<PageHeader crumbs={crumbs} labels={Copies.header(language)} headingRef={arrival.heading} {...header} />} aside={aside} label={aside ? text('capture.facts') : null}>
			<Outages reads={reads} whole={!capture} text={text} language={language} />
			{children}
		</Page>
	);

	if (!capture && refused?.code === 'NOT_FOUND') {
		return page(
			<div data-missing>
				<Unavailable kind="access" title={text('unavailable.capture.title')} reason={text('unavailable.capture.reason')} owner={text('unavailable.capture.owner')} labels={Copies.unavailable(language)} details={Problems.details(refused)} action={<Button variant="primary" href="/" label={text('action.back')} />} />
			</div>,
			{ title: text('capture.title') }
		);
	}
	if (!capture && refused) return page(<Failure refusal={refused} text={text} locale={language} attempt="read" actions={<Button href={from ?? '/'} label={text('action.back')} />} />, { title: text('capture.title') });
	// Nothing read yet and nobody answered: the line is the page's body
	if (!capture && missed) return page(null, { title: text('capture.title') });
	// Its packages and its facts wait in their own places, under the header already drawn (LOAD-01); the
	// header's actions depend on the snapshot's state and the person's role, so none is offered before it is read
	if (!capture) {
		return page(
			<Section title={text('capture.packages')} waiting={{ label: Loading.text({ name: text('loading.capture'), language }), language, rows: 2 }} />,
			{ title: text('capture.title') },
			<Waiting language={language} kind="lines" rows={4} />
		);
	}

	const links = new Links(descriptor, { packages: config.packages });
	const edits = Policy.edits(identity, capture.organization);
	const publish = links.publish(capture);
	const open = links.packages(capture);
	const project = (
		<Link to={Address.of({ project: capture.project })} className="text-ink underline" data-project={capture.project}>
			{names.name(capture.project) ?? text('nav.project.unknown')}
		</Link>
	);
	const header = {
		title: named!,
		status: <SnapshotStatus capture={capture} />,
		facts: (
			<span>
				{project} · {text('capture.facts.created', { date: date(capture.created) })} · {size(Describe.size(capture))}
			</span>
		),
		actions: (
			<span className="inline-flex flex-wrap items-center gap-2" data-next>
				{publish && <Button variant="primary" href={publish} label={text('capture.publish')} />}
				{open && <Button href={open} label={text('capture.open')} />}
				{previous && capture.state === 'active' && <Button href={`/compare?${new URLSearchParams({ a: previous, b: capture.id })}`} label={text('capture.compare.previous')} />}
				<Lifecycle capture={capture} busy={busy} act={act} settle={settle} />
			</span>
		)
	};
	const configured = Object.keys(capture.configuration ?? {}).length > 0;

	return page(
		<OutageScope missing={missing}>
			<article className="flex flex-col gap-6" data-capture={capture.id} data-state={capture.state}>
				{/* What is shown was read earlier: when, while the snapshot is not read again */}
				{missed && <Updated time={snapshot.at} language={language} />}
				{refused && <Failure refusal={refused} text={text} locale={language} attempt="read" />}
				{/* Nothing was changed when the service answered that it could not: Try again is the person's, never by itself.
				    With no answer it may have been made, and the way on is to check the snapshot, read again */}
				{problem && <Failure refusal={problem} text={text} locale={language} size={size} enable={links.enablement.state === 'off'} again={last.current} check={{ label: text('problem.check'), onSelect: snapshot.again }} />}
				{Condition.ended(capture) ? <Ended capture={capture} /> : awaited.current && <Pending capture={capture} onRead={snapshot.keep} failing={Boolean(missed)} onMissed={snapshot.miss} />}

				<Packages capture={capture} />

				{configured && (
					<TechnicalDetails
						text={`${text('capture.configuration.hint')}\n\n${JSON.stringify(capture.configuration, null, 2)}`}
						labels={configuration}
					/>
				)}

				{/* A title left empty is a step not taken yet (D183): Save title waits quiet and reachable, and a press
				    only puts the caret back in it, saying nothing; the change's own answer is the page's */}
				{edits && capture.state !== 'deleted' && (
					<FocusedForm className="flex flex-wrap items-end gap-3" onSubmit={() => act(() => captures.retitle(capture.id, snapshot.title), () => text('capture.retitled.outcome'))}>
						<div className="min-w-[min(24rem,100%)] flex-1">
							<Field label={text('capture.title.label')}>
								<input required value={snapshot.title} maxLength={200} onChange={event => snapshot.type(event.target.value)} />
							</Field>
						</div>
						<Button type="submit" label={text('action.rename')} disabled={busy} />
					</FocusedForm>
				)}
			</article>
		</OutageScope>,
		header,
		<CaptureFacts capture={capture} />
	);
}
